import React, { useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  getGetFaceQueryOptions,
  useGetFace,
  useGetLists,
  usePatchFace,
} from '@vlabs/api/artemy/client';
import type { PatchFaceMutationBody } from '@vlabs/api/artemy/client';
import type { FaceId } from '@vlabs/api/artemy/model';
import { Control } from '@vlabs/uikit';
import type { TOption } from '@vlabs/uikit/types';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import st from './EditFaceInfoForm.module.sass';
import { FaceFieldsPoint3 } from './FaceFields.point3';
import { LinkedToCardFaceFieldsPoint3 } from './LinkedToCardFaceFields.point3';
import { ChangeFacePhoto } from '../../change-face-photo';

interface EditFaceInfoFormProps {
  faceId: FaceId;
  onCancel: VoidFunction;
  onSubmit?: VoidFunction;
}

type TEditFaceInfoFormValues = Pick<PatchFaceMutationBody, 'user_data'>;

export function EditFaceInfoFormPoint3({
  faceId,
  onCancel,
  onSubmit = undefined,
}: EditFaceInfoFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const getFace = useGetFace(faceId);
  const getLists = useGetLists({ page_size: 1000 });
  const patchFace = usePatchFace();
  const [changePhotoCallback, setChangePhotoCallback] = useState<() => Promise<void>>();

  const listOptions: TOption[] = useMemo(() => getLists.data?.lists?.map(({ list_id, user_data }) => ({
    label: user_data || list_id,
    value: list_id,
  })) ?? [], [getLists.data?.lists]);

  const faceData = getFace.data;
  const hasCardId = Boolean(faceData?.card_id);
  const formValues = useMemo<TEditFaceInfoFormValues>(() => ({
    user_data: faceData?.user_data ?? '',
  }), [faceData?.user_data]);

  const form = useForm<TEditFaceInfoFormValues>({
    defaultValues: {
      user_data: '',
    },
  });

  const { reset, handleSubmit } = form;

  useEffect(() => {
    if (!faceData) return;
    reset(formValues);
  }, [faceData, formValues, reset]);

  const submitHandler: SubmitHandler<TEditFaceInfoFormValues> = async ({ user_data }) => {
    const promises = [
      patchFace.mutateAsync({ faceId, data: { user_data } }),
    ];
    if (changePhotoCallback) promises.push(changePhotoCallback());
    await Promise.all(promises);

    await queryClient.invalidateQueries(getGetFaceQueryOptions(faceId));
    toast.success(t('face-page:edit_face_info.success_toast'));
    onSubmit?.();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className={st.Root}>
          <h5>{t('face-page:edit_face_info.title')}</h5>

          <div className={st.Content}>
            <div className={st.PreviewSection}>
              <ChangeFacePhoto
                faceId={faceId}
                onChangeFacePhoto={(sideEffect) => setChangePhotoCallback(() => sideEffect)}
              />
            </div>

            {!hasCardId && <FaceFieldsPoint3 />}
            {!!hasCardId && <LinkedToCardFaceFieldsPoint3 listOptions={listOptions} />}
          </div>

          <div className={st.Divider} />

          <div className={st.Footer}>
            <Control.Button
              data-testid="submitFaceEditButton"
              type="submit"
            >
              {t('face-page:common.save')}
            </Control.Button>

            <Control.Button
              data-testid="closeFaceEditButton"
              kind="secondary"
              onClick={onCancel}
              type="button"
              variant="outlined"
            >
              {t('face-page:common.cancel')}
            </Control.Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
